/**
 * Elects a single "primary" tab among all open tabs of the app using the Web
 * Locks API. Exactly one tab holds the lock at any time; when that tab is
 * closed, navigated away, or crashes the lock is released automatically and a
 * waiting tab takes over.
 *
 * This is used so that only one tab surfaces an OS notification (and plays the
 * fallback sound) when a realtime event is broadcast to every open tab via the
 * shared realtime worker. Without it, N open tabs each call
 * `new Notification()` with the same tag; the browser then coalesces them by
 * replacing rather than re-alerting, so no fresh popup/sound appears even
 * though the entry shows up in the OS notification centre.
 *
 * On browsers without the Web Locks API every tab reports itself as primary,
 * preserving the previous (tag-deduplicated) behaviour.
 */
export class NotificationLeader {
  private static readonly lockName = "volateq-notification-primary-tab";

  private static primary = !NotificationLeader.isSupported();
  private static started = false;

  private static isSupported(): boolean {
    return typeof navigator !== "undefined" && "locks" in navigator;
  }

  /**
   * Begins competing for primary-tab status. Idempotent — safe to call from
   * every tab and more than once per tab.
   */
  static start(): void {
    if (NotificationLeader.started || !NotificationLeader.isSupported()) {
      return;
    }
    NotificationLeader.started = true;

    // Acquiring the lock marks this tab as primary. The callback returns a
    // promise that never resolves, so the lock is held for the tab's entire
    // lifetime and only released when the tab closes — at which point another
    // waiting tab acquires it and becomes primary.
    void navigator.locks.request(NotificationLeader.lockName, () => {
      NotificationLeader.primary = true;
      return new Promise<void>(() => {
        // Intentionally never resolves.
      });
    });
  }

  /** True when this tab is the one responsible for showing OS notifications. */
  static get isPrimary(): boolean {
    return NotificationLeader.primary;
  }
}
